import { useState, useEffect, useMemo, useRef, Key, JSX } from 'react';
import { PageProps } from '@inertiajs/inertia';
import { Head, usePage, router } from '@inertiajs/react';
import { useUser } from '../../src/context/UserContext';
import { useForm } from '@mantine/form';
import {
  Container,
  TextInput,
  Radio,
  RadioGroup,
  Button,
  MantineProvider,
  createTheme,
  Box,
  Paper,
  Title,
  MultiSelect,
  Accordion,
  Text,
  Avatar,
  Tabs,
  Stack,
  Select,
  Alert,
  SegmentedControl,
  ActionIcon,
  Group,
  InputLabel,
  Badge,
  LoadingOverlay,
  Tooltip,
  Card,
} from '@mantine/core';
import { DatePickerInput, TimeInput } from '@mantine/dates';
import { notifications, Notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconCheck,
  IconLink,
  IconBrandInstagram,
  IconBrandFacebook,
  IconTicket,
  IconMapPinFilled,
  IconCalendarTime,
  IconCalendarPlus,
  IconList,
  IconUsersGroup,
  IconInfoCircle,
  IconClock,
  IconAlertTriangle,
  IconCopy,
  IconUsers,
  IconClipboardText,
  IconMail,
  IconChartBar,
  IconDots,
} from '@tabler/icons-react';
import { RichTextEditor } from '@mantine/tiptap';
import { useEditor, EditorContent } from '@tiptap/react';
import Highlight from '@tiptap/extension-highlight';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Heading from '@tiptap/extension-heading';
import Link from '@tiptap/extension-link';
import timezoneData from '../../entrypoints/utilities/timezones.json';
import ImageUploadModal from '../../components/ImageUploadModal';
import DangerZoneCard from '../../components/DangerZoneCard';
import { format } from 'date-fns-tz';
import AddressAutocomplete from '../../components/AddressAutocompleteMap';
import { parseISO } from 'date-fns';
import { useScrollableTabs } from '../../src/hooks/useScrollableTabs';

import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '../../styles/Login-form.css';
import '@mantine/tiptap/styles.css';
import '@mantine/dates/styles.css';
import '../../styles/ScrollTabs.css';

// Type definitions
type Artist = {
  id: number;
  name: string;
  avatar_url: string;
};

interface User {
  id: number;
  email: string;
  username: string;
  avatar_url: string | null;
  roles: string[];
  organizer_ids?: number[];
}

interface EventType {
  id: number;
  name: string;
}

interface DanceStyle {
  id: number;
  name: string;
}

interface EventData {
  id: number;
  name: string;
  slug?: string;
  description?: string;
  address?: string;
  online_event?: boolean;
  is_recurring?: boolean;
  spans_next_day?: boolean;
  timezone?: string;
  area_id: number | null;
  event_start_date?: string;
  event_end_date?: string;
  website_url?: string;
  ticket_url?: string;
  facebook_url?: string;
  event_type_ids?: number[];
  dance_style_ids?: number[];
  event_frequency_id?: number;
  tiktok_url: string;
  meetup_url: string;
  instagram_url: string;
  event_image_url: string | null;
  artists_ids?: number[];
  user_id?: number;
  event_status_id?: number;
  organizer_ids?: number[];
  event_image?: File | null;
  event_start_time?: string;
  event_end_time?: string;
}

interface EventFrequency {
  id: number;
  name: string;
}

interface RecurringEvent {
  id: number;
  title: string;
}

interface EditEventPageProps extends PageProps {
  event: {
    id: number;
    name: string;
    slug?: string;
    description?: string;
    address?: string;
    online_event?: boolean;
    is_recurring?: boolean;
    spans_next_day?: boolean;
    timezone?: string;
    event_start_date?: string;
    event_end_date?: string;
    event_start_time?: string;
    event_end_time?: string;
    website_url?: string;
    ticket_url?: string;
    facebook_url?: string;
    event_type_ids?: number[];
    dance_style_ids?: number[];
    event_frequency_id?: number;
    tiktok_url: string;
    meetup_url: string;
    instagram_url: string;
    event_image_url: string;
    artists?: Artist[];
    artists_ids?: number[];
    dance_style?: DanceStyle[];
    recurring_event?: RecurringEvent;
    user_id?: number;
    event_status_id?: number;
    organizer_ids?: number[];
    area?: { id: number; name: string };
    artist_ids?: number[];
  };
  areas: [string, number][];
  artists?: Artist[];
  event_types: EventType[];
  dance_styles: DanceStyle[];
  event_frequencies: EventFrequency[];
  organizers?: Array<{ id: number; name: string }>;
  errors?: Record<string, string[]>;
  users?: Array<[string, number]>;
  event_statuses?: Array<{ id: number; name: string }>;
}

const theme = createTheme({
  primaryColor: 'orange',
  colors: {
    orange: [
      '#FEF5ED',
      '#FDE3D1',
      '#FCB693',
      '#FA9D73',
      '#F87F4D',
      '#F66A33',
      '#EA580C',
      '#C44B0A',
      '#9E3D08',
      '#783006',
    ],
  },
  fontFamily: 'Poppins, sans-serif',
  headings: { fontFamily: 'Poppins, sans-serif' },
  components: {
    InputLabel: {
      styles: (theme: { fontSizes: { sm: any } }) => ({
        label: {
          color: '#FFF7ED',
          fontSize: theme.fontSizes.sm,
          fontWeight: 500,
        },
      }),
    },
    Input: {
      styles: () => ({
        input: {
          backgroundColor: '#686868',
          color: '#F2F2F2',
          borderColor: '#787878',
          '&::placeholder': {
            color: '#fff',
          },
        },
      }),
    },
  },
});

export default function EditEvent() {
  // Context and page hooks
  const { user, refreshUser } = useUser() as { user: User | null; refreshUser: () => Promise<void> };
  const { props } = usePage<EditEventPageProps>();

  // Tab state
  const [activeTab, setActiveTab] = useState('overview');

  const { tabListReference } = useScrollableTabs();

  // State hooks
  const [isLoading, setIsLoading] = useState(false);
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [isLoadingTimezones, setIsLoadingTimezones] = useState(true);
  const [timezoneOptions, setTimezoneOptions] = useState<{ value: string; label: string }[]>([]);
  const [timezone, setTimezone] = useState('');
  const [address, setAddress] = useState('');
  const [seoImagePreview, setSeoImagePreview] = useState<string | null>(props.event.event_image_url);
  const [values, setValues] = useState<EventData>(props.event);
  const [croppedImage, setCroppedImage] = useState<File | null>(null);

  // Duplication loading states
  const [isDuplicatingEvent, setIsDuplicatingEvent] = useState(false);
  const [isDuplicatingRecurring, setIsDuplicatingRecurring] = useState(false);

  // Destructure props
  const {
    event,
    errors,
    artists: propArtists,
    event_types: propEventTypes,
    dance_styles: propDanceStyles,
    event_frequencies: propEventFrequencies,
    users: propUsers,
    event_statuses: propEventStatuses,
    organizers: propOrganizers,
  } = props;

  // Status mapping
  const statusMapping: Record<number, { status: string; color: string; icon: JSX.Element }> = {
    1: { status: 'Active', color: 'green', icon: <IconCheck size={16} /> },
    2: { status: 'Pending', color: 'yellow', icon: <IconAlertTriangle size={16} /> },
    3: { status: 'Draft', color: 'blue', icon: <IconInfoCircle size={16} /> },
  };

  const eventStatus =
    event.event_status_id && statusMapping[event.event_status_id]
      ? statusMapping[event.event_status_id]
      : { status: 'Unknown', color: 'gray', icon: <IconInfoCircle size={16} /> };

  // Permission checks
  const canDuplicate = useMemo(() => {
    if (!user) return false;
    if (user.roles?.includes('SuperAdmin')) return true;
    if (user.roles?.includes('Editor')) return true;
    if (event.user_id === user.id) return true;
    if (event.organizer_ids?.includes(user.id)) return true;
    return false;
  }, [user, event]);

  // Time picker refs and controls
  const startTimeRef = useRef<HTMLInputElement>(null);
  const endTimeRef = useRef<HTMLInputElement>(null);

  const startPickerControl = (
    <ActionIcon variant="subtle" color="gray" onClick={() => startTimeRef.current?.showPicker()}>
      <IconClock size={16} stroke={1.5} />
    </ActionIcon>
  );

  const endPickerControl = (
    <ActionIcon variant="subtle" color="gray" onClick={() => endTimeRef.current?.showPicker()}>
      <IconClock size={16} stroke={1.5} />
    </ActionIcon>
  );

  // Constants
  const DRAFT_STATUS_ID = 3;
  const isDraft = event.event_status_id === DRAFT_STATUS_ID;

  // Helper functions
  const getDatePortion = (isoString: string) => {
    if (!isoString) return null;
    return isoString.split('T')[0];
  };

  const getIanaTimezone = (timezoneLabel: string): string => {
    const normalizedLabel = timezoneLabel.replace(/\s+/g, '_');
    const entry = Object.entries(timezoneData).find(([value, label]) => value === normalizedLabel);
    if (entry) {
      const [ianaTimezone] = entry;
      return ianaTimezone;
    } else {
      console.warn('⚠️ No match found, defaulting to UTC!');
      return 'UTC';
    }
  };

  function validateURL(input: string) {
    const urlPattern = /^(http:\/\/|https:\/\/)/i;
    return urlPattern.test(input);
  }

  // Form hook
  const form = useForm({
    initialValues: {
      name: event.name || '',
      slug: event.slug || '',
      area_id: event.area?.id || null,
      description: event.description || '',
      address: event.address || '',
      timezone: event.timezone || '',
      online_event: event.online_event ? 'true' : 'false',
      is_recurring: event.is_recurring ? 'true' : 'false',
      spans_next_day: event.spans_next_day ? 'true' : 'false',
      event_start_date: event.event_start_date,
      event_start_time: event.event_start_time,
      event_end_time: event.event_end_time,
      event_end_date: event.event_end_date ? getDatePortion(event.event_end_date) : null,
      website_url: event.website_url || '',
      ticket_url: event.ticket_url || '',
      facebook_url: event.facebook_url || '',
      instagram_url: event.instagram_url || '',
      meetup_url: event.meetup_url || '',
      tiktok_url: event.tiktok_url || '',
      event_type_ids: event.event_type_ids || [],
      artistIds: event.artist_ids || [],
      dance_style_ids: event.dance_style_ids || [],
      event_frequency_id: event.event_frequency_id || '',
      user_id: event.user_id || null,
      event_status_id: event.event_status_id || null,
      organizer_ids: event.organizer_ids || [],
      event_image_url: event.event_image_url || null,
    },
    validate: {
      name: (value) => (value ? null : 'Event name is required'),
      slug: (value) => (value ? null : 'Slug is required'),
      description: (value) => {
        if (editor) {
          const plainText = editor.getText();
          if (plainText.length < 20) {
            return 'Description must be at least 20 characters long';
          }
        }
        return null;
      },
      event_start_date: (value) => (value ? null : 'Event start date is required'),
      event_start_time: (value) => (value ? null : 'Event start time is required'),
      timezone: (value) => (value ? null : 'Timezone is required'),
      event_type_ids: (value) =>
        Array.isArray(value) && value.length > 0 ? null : 'At least one event type is required',
      dance_style_ids: (value) =>
        Array.isArray(value) && value.length > 0 ? null : 'At least one dance style is required',
    },
    validateInputOnChange: true,
  });

  // Editor hook
  const editor = useEditor({
    extensions: [
      StarterKit,
      Highlight,
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Heading.configure({ levels: [1, 2, 3] }),
      Link.configure({ openOnClick: true }),
    ],
    content: form.values.description,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      form.setFieldValue('description', html);
    },
  });

  // Derived state
  const event_types = Array.isArray(propEventTypes) ? propEventTypes : [];
  const artists = Array.isArray(propArtists) ? propArtists : [];
  const dance_styles = Array.isArray(propDanceStyles) ? propDanceStyles : [];
  const event_frequencies = Array.isArray(propEventFrequencies) ? propEventFrequencies : [];
  const users = Array.isArray(propUsers) ? propUsers : [];
  const event_statuses = Array.isArray(propEventStatuses) ? propEventStatuses : [];

  const filteredOrganizers = useMemo(() => {
    const organizers = user?.roles?.includes('SuperAdmin')
      ? propOrganizers || []
      : (propOrganizers || []).filter((org) => user?.organizer_ids?.includes(org.id));

    return [...organizers].sort((a, b) => a.name.localeCompare(b.name));
  }, [propOrganizers, user]);

  const is_superadmin = user?.roles?.includes('SuperAdmin') || false;

  const frequencyOrder = ['One Time', 'Weekly', 'Every Two Weeks', 'Monthly', 'Annual'];
  const sortedEventFrequencies = event_frequencies.sort((a, b) => {
    return frequencyOrder.indexOf(a.name) - frequencyOrder.indexOf(b.name);
  });

  // Tab definitions
  const tabs = [
    { id: 'overview', label: 'Overview', icon: <IconCalendarPlus size={16} /> },
    { id: 'attendees', label: 'Attendees', icon: <IconUsers size={16} /> },
    { id: 'registration', label: 'Registration', icon: <IconClipboardText size={16} /> },
    { id: 'blasts', label: 'Blasts', icon: <IconMail size={16} /> },
    { id: 'insights', label: 'Insights', icon: <IconChartBar size={16} /> },
    { id: 'more', label: 'More', icon: <IconDots size={16} /> },
  ];

  const handleTabChange = (tab: string | null) => {
    if (tab) {
      setActiveTab(tab);
    }
  };

  // Event handlers
  const handleStartTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const timeValue = e.target.value;
    form.setFieldValue('event_start_time', timeValue);
    setValues((prevValues) => ({
      ...prevValues,
      event_start_time: timeValue,
    }));
  };

  const handleEndTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const timeValue = e.target.value;
    form.setFieldValue('event_end_time', timeValue);
    setValues((prevValues) => ({
      ...prevValues,
      event_end_time: timeValue,
    }));
  };

  const handleImageChange = (file: File) => {
    setValues((prevValues) => {
      const newValues = { ...prevValues, event_image: file };
      return newValues;
    });
    form.setFieldValue('event_image', file);
    setSeoImagePreview(URL.createObjectURL(file));
  };

  const handleAddressChange = (newAddress: string) => {
    form.setFieldValue('address', newAddress);
    setValues((prevValues) => ({
      ...prevValues,
      address: newAddress,
    }));
  };

  // Duplication functions
  const duplicateEvent = async (duplicateAs: 'single' | 'recurring') => {
    if (duplicateAs === 'single') {
      setIsDuplicatingEvent(true);
    } else {
      setIsDuplicatingRecurring(true);
    }

    try {
      const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content;
      if (!csrfToken) {
        throw new Error('CSRF token not found');
      }

      const response = await fetch(`/events/${event.id}/duplicate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({ duplicate_as: duplicateAs }),
      });

      const data = await response.json();

      if (data.success) {
        notifications.show({
          title: 'Success',
          message: 'Event duplicated successfully. Please review and submit.',
          color: 'green',
          icon: <IconCheck />,
          autoClose: 5000,
          position: 'top-right',
        });

        setTimeout(() => {
          // Use the redirect_url if provided by the server, otherwise construct it
          if (data.redirect_url) {
            window.location.href = data.redirect_url;
          } else {
            // Fixed URL construction - use query parameter format
            window.location.href = `/suggest_event?edit_draft=${data.event_id}`;
          }
        }, 1000);
      } else {
        throw new Error(data.error || 'Failed to duplicate event');
      }
    } catch (error) {
      console.error('Duplication error:', error);
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'An error occurred while duplicating the event',
        color: 'red',
        icon: <IconAlertCircle />,
        autoClose: 5000,
      });
    } finally {
      setIsDuplicatingEvent(false);
      setIsDuplicatingRecurring(false);
    }
  };

  // Form submission handler
  const handleSubmit = async (submittedValues: typeof form.values & { event_image?: File; draft?: boolean }) => {
    setIsLoading(true);
    setOverlayVisible(true);

    const isDraft =
      submittedValues.draft === true ||
      eventStatus.status === 'Draft' ||
      form.values.event_status_id === DRAFT_STATUS_ID;

    try {
      const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content;
      if (!csrfToken) {
        console.error('CSRF token not found');
        return;
      }

      const formData = new FormData();

      // Handle date/time fields
      if (form.values.event_start_date && form.values.event_start_time) {
        const rawStartDate = form.values.event_start_date;
        const startDatePortion =
          rawStartDate && typeof rawStartDate === 'string' && rawStartDate.includes('T')
            ? rawStartDate.split('T')[0]
            : rawStartDate;
        const combinedStart = `${startDatePortion}T${form.values.event_start_time}:00`;
        formData.append('event[event_start_date]', combinedStart);
      }

      if (form.values.event_end_date && form.values.event_end_time) {
        const rawEndDate = form.values.event_end_date;
        const endDatePortion =
          rawEndDate && typeof rawEndDate === 'string' && rawEndDate.includes('T')
            ? rawEndDate.split('T')[0]
            : rawEndDate;
        const combinedEnd = `${endDatePortion}T${form.values.event_end_time}:00`;
        formData.append('event[event_end_date]', combinedEnd);
      }

      // Basic fields
      formData.append('event[name]', form.values.name);
      if (form.values.slug) formData.append('event[slug]', form.values.slug);
      if (form.values.address) formData.append('event[address]', form.values.address);
      if (form.values.timezone) formData.append('event[timezone]', form.values.timezone);

      if (editor && typeof editor.getHTML === 'function') {
        formData.append('event[description]', editor.getHTML());
      }

      if (submittedValues.event_image) {
        formData.append('event[event_image]', submittedValues.event_image);
      }

      // Boolean fields
      formData.append('event[online_event]', form.values.online_event === 'true' ? 'true' : 'false');
      formData.append('event[is_recurring]', form.values.is_recurring === 'true' ? 'true' : 'false');
      formData.append('event[spans_next_day]', form.values.spans_next_day === 'true' ? 'true' : 'false');

      // Array fields
      if (Array.isArray(form.values.event_type_ids) && form.values.event_type_ids.length > 0) {
        form.values.event_type_ids.forEach((id) => formData.append('event[event_type_ids][]', id.toString()));
      }

      if (Array.isArray(form.values.dance_style_ids) && form.values.dance_style_ids.length > 0) {
        form.values.dance_style_ids.forEach((id) => formData.append('event[dance_style_ids][]', id.toString()));
      }

      if (Array.isArray(form.values.artistIds) && form.values.artistIds.length > 0) {
        form.values.artistIds.forEach((id) => formData.append('event[artist_ids][]', id.toString()));
      }

      if (Array.isArray(form.values.organizer_ids)) {
        if (form.values.organizer_ids.length > 0) {
          form.values.organizer_ids.forEach((id) => formData.append('event[organizer_ids][]', id.toString()));
        } else {
          formData.append('event[organizer_ids][]', '');
        }
      }

      if (form.values.area_id) {
        formData.append('event[area_id]', form.values.area_id.toString());
      }

      if (form.values.event_frequency_id) {
        formData.append('event[event_frequency_id]', form.values.event_frequency_id.toString());
      }

      if (isDraft) {
        formData.append('save_changes', 'true');
      } else {
        formData.append('submit_for_approval', 'true');
      }

      const response = await fetch(`/events/${props.event.id}`, {
        method: 'PUT',
        headers: { Accept: 'application/json', 'X-CSRF-Token': csrfToken },
        credentials: 'include',
        body: formData,
      });

      if (response.ok) {
        notifications.show({
          title: 'Update Successful',
          message: isDraft ? 'Event draft was successfully saved!' : 'Event was successfully updated!',
          color: 'green',
          icon: <IconCheck />,
          autoClose: 6000,
          position: 'top-right',
        });

        setTimeout(() => (window.location.href = `/events/${form.values.slug}`), 1500);
      } else {
        const data = await response.json().catch(() => ({}));

        notifications.show({
          title: 'Submission Failed',
          message: data.error || 'An error occurred while updating the event.',
          color: 'red',
          icon: <IconAlertCircle />,
          autoClose: 5000,
          position: 'top-right',
        });

        if (data.errors) form.setErrors(data.errors);
      }
    } catch (error) {
      notifications.show({
        title: 'Unexpected Error',
        message: 'An unexpected error occurred. Please try again.',
        color: 'red',
        icon: <IconAlertCircle />,
        autoClose: 5000,
        position: 'top-right',
      });

      console.error('Error updating event:', error);
    } finally {
      setIsLoading(false);
      setOverlayVisible(false);
    }
  };

  // Effects
  const formatOffset = (offsetSeconds: number) => {
    const hours = Math.floor(Math.abs(offsetSeconds) / 3600);
    const minutes = Math.floor((Math.abs(offsetSeconds) % 3600) / 60);
    const sign = offsetSeconds >= 0 ? '+' : '-';
    return `GMT${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    const fetchTimezones = async () => {
      setIsLoadingTimezones(true);
      try {
        const apiKey = 'N2IE9BKU2EQR';
        const response = await fetch(`https://api.timezonedb.com/v2.1/list-time-zone?key=${apiKey}&format=json`);
        const data = await response.json();

        if (data.status === 'OK' && data.zones && Array.isArray(data.zones)) {
          const options = data.zones.map((zone: any) => ({
            value: zone.zoneName,
            label: `${zone.zoneName} (${formatOffset(zone.gmtOffset)})`,
          }));

          setTimezoneOptions(options);

          if (form.values.timezone) {
            const matchedTimezone = options.find((option) => option.value === form.values.timezone);
            if (matchedTimezone) {
              form.setFieldValue('timezone', matchedTimezone.value);
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch timezones:', error);
      } finally {
        setIsLoadingTimezones(false);
      }
    };

    fetchTimezones();
  }, []);

  useEffect(() => {
    if (event) {
      setSeoImagePreview(event.event_image_url);
    }
  }, [event]);

  useEffect(() => {
    if (user) {
      setIsLoading(false);
    }

    if (Object.keys(form.errors).length > 0) {
      const firstErrorKey = Object.keys(form.errors)[0];
      const firstErrorMessage = form.errors[firstErrorKey];

      notifications.show({
        title: 'Validation Error',
        message: firstErrorMessage || 'Please fix the errors before submitting.',
        color: 'red',
        icon: <IconAlertCircle />,
        autoClose: 5000,
        position: 'top-right',
      });
    }
  }, [user, form.errors]);

  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <Box>
            {/* Admin Info Section */}
            {is_superadmin && (
              <Paper
                shadow="md"
                p="md"
                mb="lg"
                style={{ backgroundColor: 'gray', borderRadius: '8px' }}
                className="mt-3"
              >
                <Stack gap="xs">
                  <Group gap="xs">
                    <Text fw={700} size="xl" style={{ color: '#FFF7ED' }}>
                      Admin Info
                    </Text>
                    <Badge color="orange" variant="filled" size="lg">
                      ID: {event.id}
                    </Badge>
                  </Group>

                  <Select
                    label="Which user is claiming this event?"
                    name="user_id"
                    styles={{ label: { color: '#FFF7ED' } }}
                    data={users.map(([username, id]) => ({ value: id.toString(), label: username }))}
                    value={form.values.user_id ? form.values.user_id.toString() : null}
                    onChange={(value) => form.setFieldValue('user_id', value ? Number(value) : null)}
                    searchable
                    clearable
                  />

                  <Select
                    label="What is the status of this event?"
                    name="event_status_id"
                    styles={{ label: { color: '#FFF7ED' } }}
                    data={event_statuses.map((status) => ({ value: status.id.toString(), label: status.name }))}
                    value={form.values.event_status_id ? form.values.event_status_id.toString() : null}
                    onChange={(value) => form.setFieldValue('event_status_id', value ? Number(value) : null)}
                    clearable
                  />
                </Stack>
              </Paper>
            )}

            {/* Status Alert */}
            <Alert
              icon={eventStatus.icon}
              title={`Event Status: ${eventStatus.status}`}
              color={eventStatus.color}
              variant="filled"
              mb="md"
            />

            {/* Recurring Event Alert */}
            {event.recurring_event && (
              <Alert
                icon={<IconInfoCircle size="1.5rem" />}
                title="Editing a Recurring Event Instance"
                color="orange"
                radius="sm"
                variant="filled"
                mb="md"
                mt="md"
              >
                <Text>
                  You are currently editing a single instance of the recurring event:{' '}
                  <strong>{event.recurring_event.title}</strong>.
                </Text>
                <Text mt="sm">
                  These changes will <strong>not</strong> affect the other occurrences of this event.
                </Text>
              </Alert>
            )}

            {/* Image Upload */}
            <ImageUploadModal previewUrl={seoImagePreview} onImageChange={handleImageChange} />

            {/* All the original accordion sections */}
            <Accordion multiple className="form-accordion">
              <Accordion.Item value="event-info">
                <Accordion.Control>
                  <Group gap="xs">
                    <IconCalendarPlus size={28} />
                    <Text style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>Event Host & Name</Text>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <MultiSelect
                    label="Event Host"
                    hidePickedOptions
                    placeholder="Select event organizers"
                    name="organizer_ids"
                    data={filteredOrganizers.map((org) => ({ value: org.id.toString(), label: org.name }))}
                    value={(form.values.organizer_ids || []).map((id) => id.toString())}
                    onChange={(values) => {
                      const numericValues = Array.isArray(values) ? values.map((v) => Number(v)) : [];
                      form.setFieldValue('organizer_ids', numericValues);
                    }}
                    searchable
                    clearable
                    mt="md"
                    styles={{ label: { color: '#FFF7ED' } }}
                  />
                  <TextInput
                    label="Event Name"
                    name="name"
                    placeholder="Enter the name"
                    {...form.getInputProps('name')}
                    mt="md"
                    required
                    styles={{ label: { color: '#FFF7ED' } }}
                  />
                  <TextInput
                    label="Slug"
                    name="slug"
                    placeholder="your-event-slug"
                    {...form.getInputProps('slug')}
                    mt="md"
                    required
                    styles={{ label: { color: '#FFF7ED' } }}
                  />
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>

            <Accordion multiple className="form-accordion">
              <Accordion.Item value="location-info">
                <Accordion.Control>
                  <Group gap="xs">
                    <IconMapPinFilled size={28} />
                    <Text style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>Location Info</Text>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <Text size="sm" fw={500} mt={3} style={{ color: '#FFF7ED' }}>
                    Is this an online event or in-person event?
                  </Text>
                  <SegmentedControl
                    name="online_event"
                    color="orange"
                    size="sm"
                    data={[
                      { label: 'In Person', value: 'false' },
                      { label: 'Online', value: 'true' },
                    ]}
                    {...form.getInputProps('online_event')}
                    style={{ border: '2px solid orange', backgroundColor: 'white' }}
                  />

                  {form.values.online_event === 'false' && (
                    <AddressAutocomplete
                      value={form.values.address}
                      onChange={handleAddressChange}
                      onTimezoneSelect={(newTimezone) => {
                        setTimezone(newTimezone);
                        form.setFieldValue('timezone', newTimezone);
                      }}
                      timezoneValue={form.values.timezone}
                    />
                  )}
                  <Select
                    label="Area"
                    placeholder="Select the event area"
                    data={(props.areas || []).map(([name, id]) => ({ value: id.toString(), label: name }))}
                    value={form.values.area_id ? form.values.area_id.toString() : null}
                    onChange={(val) => form.setFieldValue('area_id', val ? Number(val) : null)}
                    searchable
                    clearable
                    mt="md"
                    styles={{ label: { color: '#FFF7ED' } }}
                  />
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>

            <Accordion multiple className="form-accordion">
              <Accordion.Item value="date-info">
                <Accordion.Control>
                  <Group gap="xs">
                    <IconCalendarTime size={28} />
                    <Text style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>Date & Time Info</Text>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <SegmentedControl
                    name="is_recurring"
                    color="orange"
                    size="sm"
                    disabled
                    data={[
                      { label: 'Single Event', value: 'false' },
                      { label: 'Recurring Event', value: 'true' },
                    ]}
                    {...form.getInputProps('is_recurring')}
                    style={{ border: '2px solid orange', backgroundColor: 'white' }}
                  />

                  <Group gap="md" mt="md" grow>
                    <DatePickerInput
                      label="Event Start Date"
                      placeholder="Select date"
                      name="event_start_date"
                      value={form.values.event_start_date ? new Date(form.values.event_start_date) : null}
                      onChange={(date) => {
                        if (date) {
                          const year = date.getFullYear();
                          const month = String(date.getMonth() + 1).padStart(2, '0');
                          const day = String(date.getDate()).padStart(2, '0');
                          form.setFieldValue('event_start_date', `${year}-${month}-${day}`);
                        } else {
                          form.setFieldValue('event_start_date', '');
                        }
                      }}
                      required={!isDraft}
                      styles={{ label: { color: '#FFF7ED' } }}
                    />

                    <TimeInput
                      label="Choose the event start time"
                      name="event_start_time"
                      ref={startTimeRef}
                      rightSection={startPickerControl}
                      color="orange"
                      value={form.values.event_start_time}
                      onChange={handleStartTimeChange}
                      required={!isDraft}
                      styles={{ label: { color: '#FFF7ED' } }}
                    />
                  </Group>

                  <Group gap="md" mt="md" grow>
                    <DatePickerInput
                      label="Event End Date"
                      name="event_end_date"
                      placeholder="Select date"
                      value={form.values.event_end_date ? new Date(form.values.event_end_date) : null}
                      onChange={(date) => {
                        if (date) {
                          const year = date.getFullYear();
                          const month = String(date.getMonth() + 1).padStart(2, '0');
                          const day = String(date.getDate()).padStart(2, '0');
                          form.setFieldValue('event_end_date', `${year}-${month}-${day}`);
                        } else {
                          form.setFieldValue('event_end_date', '');
                        }
                      }}
                      required={!isDraft}
                      styles={{ label: { color: '#FFF7ED' } }}
                    />
                    <TimeInput
                      label="Choose the event end time"
                      name="event_end_time"
                      ref={endTimeRef}
                      rightSection={endPickerControl}
                      color="orange"
                      value={form.values.event_end_time}
                      onChange={handleEndTimeChange}
                      required={!isDraft}
                      styles={{ label: { color: '#FFF7ED' } }}
                    />
                  </Group>
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>

            <Accordion multiple className="form-accordion">
              <Accordion.Item value="event-details">
                <Accordion.Control>
                  <Group gap="xs">
                    <IconList size={28} />
                    <Text style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>Event Details</Text>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <MultiSelect
                    label="What type of event is this?"
                    name="event_type_ids"
                    hidePickedOptions
                    styles={{ label: { color: '#FFF7ED' } }}
                    placeholder="Choose your event types"
                    data={event_types.map((type) => ({ value: type.id.toString(), label: type.name }))}
                    value={(Array.isArray(form.values.event_type_ids) ? form.values.event_type_ids : []).map((id) =>
                      id.toString(),
                    )}
                    onChange={(values) => {
                      const numericValues = values.map(Number);
                      form.setFieldValue('event_type_ids', numericValues);
                    }}
                    searchable
                    clearable
                    required={!isDraft}
                  />

                  <MultiSelect
                    label="Dance Styles"
                    name="dance_style_ids"
                    hidePickedOptions
                    styles={{ label: { color: '#FFF7ED' }, dropdown: { zIndex: 1000 } }}
                    placeholder="Choose dance styles"
                    data={dance_styles.map((style) => ({ value: style.id.toString(), label: style.name }))}
                    value={(Array.isArray(form.values.dance_style_ids) ? form.values.dance_style_ids : []).map((id) =>
                      id.toString(),
                    )}
                    onChange={(values) => {
                      const numericValues = values.map(Number);
                      form.setFieldValue('dance_style_ids', numericValues);
                    }}
                    searchable
                    clearable
                    mt="lg"
                    required={!isDraft}
                  />

                  <Select
                    label="Event Frequency"
                    name="event_frequency_id"
                    styles={{ label: { color: '#FFF7ED' } }}
                    placeholder="Select event frequency"
                    data={sortedEventFrequencies.map((freq) => ({ value: freq.id.toString(), label: freq.name }))}
                    value={form.values.event_frequency_id ? form.values.event_frequency_id.toString() : null}
                    onChange={(value) => form.setFieldValue('event_frequency_id', value ? Number(value) : null)}
                    searchable
                    clearable
                    mt="lg"
                    mb="xl"
                    required={!isDraft}
                  />

                  <Text size="sm" fw={500} style={{ color: '#FFF7ED' }}>
                    Event Description
                  </Text>
                  {editor && (
                    <RichTextEditor editor={editor} mt="">
                      <RichTextEditor.Toolbar>
                        <RichTextEditor.ControlsGroup>
                          <RichTextEditor.Bold />
                          <RichTextEditor.Italic />
                          <RichTextEditor.Underline />
                          <RichTextEditor.Strikethrough />
                        </RichTextEditor.ControlsGroup>
                        <RichTextEditor.ControlsGroup>
                          <RichTextEditor.Code />
                          <RichTextEditor.Highlight />
                          <RichTextEditor.ClearFormatting />
                        </RichTextEditor.ControlsGroup>
                        <RichTextEditor.ControlsGroup>
                          <RichTextEditor.Link />
                          <RichTextEditor.Unlink />
                        </RichTextEditor.ControlsGroup>
                        <RichTextEditor.ControlsGroup>
                          <RichTextEditor.H1 />
                          <RichTextEditor.H2 />
                          <RichTextEditor.H3 />
                        </RichTextEditor.ControlsGroup>
                        <RichTextEditor.ControlsGroup>
                          <RichTextEditor.Blockquote />
                          <RichTextEditor.Hr />
                          <RichTextEditor.BulletList />
                          <RichTextEditor.OrderedList />
                        </RichTextEditor.ControlsGroup>
                        <RichTextEditor.ControlsGroup>
                          <RichTextEditor.Undo />
                          <RichTextEditor.Redo />
                        </RichTextEditor.ControlsGroup>
                      </RichTextEditor.Toolbar>
                      <RichTextEditor.Content />
                    </RichTextEditor>
                  )}
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>

            <Accordion multiple className="form-accordion">
              <Accordion.Item value="artist-info">
                <Accordion.Control>
                  <Group gap="xs">
                    <IconUsersGroup size={28} />
                    <Text style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>Artists</Text>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <MultiSelect
                    label="Select Artists"
                    name="artistIds"
                    hidePickedOptions
                    placeholder="Choose artists"
                    styles={{ label: { color: '#FFF7ED', fontSize: '16px' } }}
                    data={artists.map((artist) => ({ value: artist.id.toString(), label: artist.name }))}
                    value={(form.values.artistIds || []).map(String)}
                    onChange={(values) => {
                      const numericValues = Array.isArray(values) ? values.map(Number) : [];
                      form.setFieldValue('artistIds', numericValues);
                    }}
                    searchable
                    clearable
                  />

                  {form.values.artistIds && form.values.artistIds.length > 0 && (
                    <Stack mt="md">
                      <Group style={{ gap: '4px', flexWrap: 'wrap' }}>
                        {form.values.artistIds.map((id: Key | null | undefined) => {
                          const artist = artists.find((a) => a.id === id);
                          return artist ? (
                            <Avatar
                              key={id}
                              src={artist.avatar_url}
                              alt={artist.name}
                              radius="50%"
                              size="xl"
                              style={{
                                boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.2)',
                                border: '2px solid white',
                              }}
                            />
                          ) : null;
                        })}
                      </Group>
                    </Stack>
                  )}
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>

            <Accordion multiple className="form-accordion">
              <Accordion.Item value="website-links">
                <Accordion.Control>
                  <Group gap="xs">
                    <IconLink size={20} />
                    <Text style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>Links</Text>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <TextInput
                    label="Website Link"
                    name="website_url"
                    styles={{ label: { color: '#FFF7ED' } }}
                    placeholder="Enter the event website."
                    {...form.getInputProps('website_url')}
                    mt="md"
                  />
                  <TextInput
                    label="Passes Link"
                    name="ticket_url"
                    styles={{ label: { color: '#FFF7ED' } }}
                    placeholder="Enter the link to purchase passes."
                    {...form.getInputProps('ticket_url')}
                    mt="md"
                  />
                  <TextInput
                    label="Facebook Event Link"
                    name="facebook_url"
                    styles={{ label: { color: '#FFF7ED' } }}
                    placeholder="Enter the link to Facebook event."
                    {...form.getInputProps('facebook_url')}
                    mt="md"
                  />
                  <TextInput
                    label="Instagram Link"
                    name="instagram_url"
                    styles={{ label: { color: '#FFF7ED' } }}
                    placeholder="Enter the link to the event Instagram page."
                    {...form.getInputProps('instagram_url')}
                    mt="md"
                  />
                  <TextInput
                    label="Meetup Link"
                    name="meetup_url"
                    styles={{ label: { color: '#FFF7ED' } }}
                    placeholder="Enter the link to Meetup event."
                    {...form.getInputProps('meetup_url')}
                    mt="md"
                  />
                  <TextInput
                    label="Tiktok Link"
                    name="tiktok_url"
                    styles={{ label: { color: '#FFF7ED' } }}
                    placeholder="Enter the link to the event Tiktok event page."
                    {...form.getInputProps('tiktok_url')}
                    mt="md"
                  />
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          </Box>
        );

      case 'attendees':
        return (
          <Box p="md">
            <Text size="md" c="white" ta="left">
              Attendees management coming soon...
            </Text>
          </Box>
        );

      case 'registration':
        return (
          <Box p="md">
            <Text size="md" c="white" ta="left">
              Registration settings coming soon...
            </Text>
          </Box>
        );

      case 'blasts':
        return (
          <Box p="md">
            <Text size="md" c="white" ta="left">
              Email & Text blasts coming soon...
            </Text>
          </Box>
        );

      case 'insights':
        return (
          <Box p="md">
            <Text size="md" c="white" ta="left">
              Event insights coming soon...
            </Text>
          </Box>
        );

      case 'more':
        return (
          <Box>
            {/* Event Duplication Section */}
            {canDuplicate && (
              <Card mb="xl" p="lg" style={{ backgroundColor: '#4a4a4a', border: '1px solid #666' }}>
                <Title order={4} c="white" mb="md">
                  Duplicate As A Single Event
                </Title>

                <Stack gap="md">
                  {/* Duplicate Event Button - Available for all events */}
                  <Tooltip label="Create a copy of this event as a single event" position="top" withArrow>
                    <Button
                      leftSection={<IconCopy size={16} />}
                      variant="filled"
                      color="orange"
                      size="md"
                      loading={isDuplicatingEvent}
                      onClick={() => duplicateEvent('single')}
                      style={{ marginLeft: 'auto', display: 'block' }}
                    >
                      Duplicate Event (Single)
                    </Button>
                  </Tooltip>

                  {/* Recurring Event Options - Only show if event is recurring */}
                  {event.is_recurring && (
                    <Box>
                      <Title order={4} c="white" my="md">
                        Recurring Event Options:
                      </Title>

                      <Stack gap="xs">
                        <Tooltip
                          label="Create a copy of this recurring event series (best for short-term series, 4-8 weeks)"
                          position="top"
                          withArrow
                        >
                          <Button
                            leftSection={<IconCopy size={16} />}
                            variant="filled"
                            color="orange"
                            loading={isDuplicatingRecurring}
                            onClick={() => duplicateEvent('recurring')}
                            size="md"
                            disabled
                            className="clear-button"
                            style={{ marginLeft: 'auto', display: 'block' }}
                          >
                            Duplicate Recurring Event Series
                          </Button>
                        </Tooltip>

                        <Tooltip
                          label="Add more events to long-standing recurring series (best for 8+ weeks series)"
                          position="top"
                          withArrow
                        >
                          <Button
                            leftSection={<IconCopy size={16} />}
                            variant="filled"
                            color="orange"
                            size="md"
                            disabled
                            className="clear-button"
                            style={{ marginLeft: 'auto', display: 'block' }}
                          >
                            Add Events to Long-term Recurring Events
                          </Button>
                        </Tooltip>
                      </Stack>
                    </Box>
                  )}
                </Stack>
              </Card>
            )}

            {/* Danger Zone */}
            <DangerZoneCard eventId={event.id} eventName={event.name} />
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <MantineProvider theme={theme}>
      <Notifications />
      <Container size="sm" style={{ minHeight: '75vh' }}>
        <Paper
          p="lg"
          shadow="xs"
          style={{ borderRadius: '8px', backgroundColor: '#3d3c3e' }}
          pos="relative"
          className="my-4"
        >
          <Title order={3} c="#E4E4E7" mb="lg">
            Edit Event: {event.name}
          </Title>

          {/* Tab Navigation */}
          <Box mb="lg">
            <Tabs value={activeTab} color="orange">
              <Tabs.List ref={tabListReference}>
                {tabs.map((tab) => (
                  <Tabs.Tab
                    key={tab.id}
                    value={tab.id}
                    variant={activeTab === tab.id ? 'filled' : 'subtle'}
                    color={activeTab === tab.id ? 'orange' : 'gray'}
                    leftSection={tab.icon}
                    onClick={() => setActiveTab(tab.id)}
                    style={{
                      color: 'white',
                      borderBottom: activeTab === tab.id ? '2px solid #EA580C' : '2px solid transparent',
                      backgroundColor: activeTab === tab.id ? '#EA580C' : 'transparent',
                      minWidth: '150px',
                      fontWeight: 500,
                      borderRadius: '4px 4px 0px 0px',
                    }}
                    size="sm"
                  >
                    {tab.label}
                  </Tabs.Tab>
                ))}
              </Tabs.List>
            </Tabs>
          </Box>

          {/* Tab Content */}
          <Box>{renderTabContent()}</Box>

          {/* Form Actions - Only show in Overview tab */}
          {activeTab === 'overview' && (
            <form onSubmit={form.onSubmit(() => handleSubmit({ ...form.values, event_image: values.event_image }))}>
              {/* Error Display */}
              {errors && Object.keys(errors).length > 0 && (
                <Box color="red" mt="md">
                  {Object.entries(errors).map(([field, messages]) => (
                    <div key={field}>
                      {field}: {messages.join(', ')}
                    </div>
                  ))}
                </Box>
              )}

              {/* Form Submission Buttons */}
              {event.event_status_id === 3 ? (
                <Group mt="xl" gap="md">
                  <Button
                    variant="light"
                    color="blue"
                    size="lg"
                    fullWidth
                    onClick={() => {
                      if (!form.values.name || form.values.name.trim() === '') {
                        notifications.show({
                          title: 'Validation Error',
                          message: 'Event name is required even for drafts.',
                          color: 'red',
                          icon: <IconAlertCircle />,
                          autoClose: 5000,
                        });
                        return;
                      }

                      handleSubmit({
                        ...form.values,
                        event_image: values.event_image,
                        draft: true,
                      });
                    }}
                  >
                    Save Changes as Draft
                  </Button>

                  <Button
                    color="orange"
                    size="lg"
                    fullWidth
                    onClick={() => {
                      const validationErrors = form.validate();
                      if (validationErrors.hasErrors) {
                        Notifications.clean();

                        Object.entries(validationErrors.errors).forEach(([label, message], index) => {
                          const formattedLabel = label
                            .replace(/_/g, ' ')
                            .split(' ')
                            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                            .join(' ');

                          setTimeout(() => {
                            notifications.show({
                              title: `Error with ${formattedLabel}`,
                              message: message,
                              color: 'red',
                              icon: <IconAlertCircle />,
                              autoClose: 8000,
                              position: 'top-right',
                            });
                          }, index * 200);
                        });

                        setIsLoading(false);
                        return;
                      }

                      handleSubmit({
                        ...form.values,
                        event_image: values.event_image,
                        draft: false,
                      });
                    }}
                  >
                    Submit Event For Approval
                  </Button>
                </Group>
              ) : (
                <Button
                  mt="xl"
                  size="lg"
                  fullWidth
                  loading={isLoading}
                  onClick={() => {
                    handleSubmit({ ...form.values, event_image: values.event_image });
                  }}
                >
                  Update Event
                </Button>
              )}
            </form>
          )}

          <LoadingOverlay visible={overlayVisible} />
        </Paper>
      </Container>
    </MantineProvider>
  );
}
